import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import './Services.css';
import { 
  BsArrowRight, 
  BsPlayFill, 
  BsBullseye, 
  BsCpu, 
  BsGraphUpArrow, 
  BsCheckCircleFill, 
  BsCodeSquare, 
  BsRobot, 
  BsMegaphone, 
  BsLayers, 
  BsPalette, 
  BsShieldCheck, 
  BsSearch, 
  BsClipboardCheck, 
  BsCodeSlash, 
  BsRocketTakeoff, 
  BsBriefcase, 
  BsPeopleFill, 
  BsClock, 
  BsShieldFillCheck, 
  BsChevronLeft, 
  BsChevronRight, 
  BsX,
  BsGlobe
} from 'react-icons/bs';
import { useCMS } from '../../context/CMSContext';

const defaultProcessSteps = [
  { num: "01", title: "Discover", desc: "Understand your goals and challenges." },
  { num: "02", title: "Plan", desc: "Strategy and roadmap tailored to your needs." },
  { num: "03", title: "Design", desc: "Creative designs that convert." },
  { num: "04", title: "Develop", desc: "Clean, scalable and secure development." },
  { num: "05", title: "Launch", desc: "Go live and grow your business." }
];

const coreServices = [
  {
    id: "web-dev",
    title: "Web Development",
    desc: "High-performance, responsive websites that convert visitors into customers.",
    image: "/assets/react-website.jpg",
    icon: <BsCodeSquare />,
    link: "/web-development",
    features: [
      "Custom Websites",
      "CMS Development",
      "Performance Optimization"
    ]
  },
  {
    id: "ai-solutions",
    title: "AI Solutions",
    desc: "Custom AI tools and automation systems to make your business smarter and faster.",
    image: "/assets/ai_robot_face.jpg",
    icon: <BsRobot />,
    link: "/ai-automation",
    features: [
      "AI Chatbots",
      "Process Automation",
      "Custom AI Integrations"
    ]
  },
  {
    id: "digital-marketing",
    title: "Digital Marketing",
    desc: "Data-driven strategies to get you more traffic, leads and revenue.",
    image: "/assets/digita-MARKETING.jpg",
    icon: <BsMegaphone />,
    link: "/digital-marketing",
    features: [
      "Social Media Marketing",
      "Google Ads",
      "SEO (Search Engine Optimization)"
    ]
  },
  {
    id: "web-apps",
    title: "Web Applications",
    desc: "Scalable web apps tailored to your unique business needs.",
    image: "/assets/project_saas.jpg",
    icon: <BsLayers />,
    link: "/software-development",
    features: [
      "Custom Web Apps",
      "SaaS Development",
      "API Integrations"
    ]
  },
  {
    id: "ui-ux",
    title: "UI/UX Design",
    desc: "Beautiful and user-friendly designs that create seamless experiences.",
    image: "/assets/services/2.png",
    icon: <BsPalette />,
    link: "/ui-ux-design",
    features: [
      "UI/UX for Web & Apps",
      "Brand & Interface Design",
      "User Research & Wireframes"
    ]
  },
  {
    id: "maintenance",
    title: "Maintenance & Support",
    desc: "Keep your digital assets secure, updated and running smoothly.",
    image: "/assets/services/3.png",
    icon: <BsShieldCheck />,
    link: "/crm-development",
    features: [
      "24/7 Support",
      "Security Updates",
      "Performance Monitoring"
    ]
  }
];

const caseStudies = [
  {
    id: "ecommerce",
    title: "E-Commerce Platform",
    category: "Web Development + Marketing",
    image: "/assets/project_ecommerce.jpg",
    link: "/portfolio"
  },
  {
    id: "aichat",
    title: "AI Chatbot Solution",
    category: "AI Solutions",
    image: "/assets/project_aichat.jpg",
    link: "/portfolio"
  },
  {
    id: "healthcare",
    title: "Healthcare Website",
    category: "Web Development + UI/UX",
    image: "/assets/project_saas.jpg",
    link: "/portfolio"
  },
  {
    id: "branding",
    title: "Brand Growth Campaign",
    category: "Digital Marketing",
    image: "/assets/project_growth.jpg",
    link: "/portfolio"
  }
];

const Services = () => {
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [caseStudyIndex, setCaseStudyIndex] = useState(0);
  const { getSection } = useCMS();

  const servicesCMS = getSection('services_page', {
    heroEyebrow: "OUR SERVICES",
    heroTitleLine1: "Complete Digital",
    heroTitleLine2: "Solutions Under",
    heroHighlight: "One Roof",
    heroSubtitle: "From websites to AI solutions, digital marketing to custom applications — we provide end-to-end services to help your business grow, scale and stay ahead.",
    processHeading: "A Simple Process for Amazing Results",
    processSubtitle: "We follow a clear and collaborative process to turn your ideas into successful digital products.",
    processSteps: defaultProcessSteps
  });

  const activeProcessSteps = (Array.isArray(servicesCMS.processSteps) && servicesCMS.processSteps.length > 0)
    ? servicesCMS.processSteps
    : defaultProcessSteps;

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      easing: 'ease-out-cubic'
    });
  }, []);

  const handleNextCase = () => {
    setCaseStudyIndex((prev) => (prev + 1) % caseStudies.length);
  };

  const handlePrevCase = () => {
    setCaseStudyIndex((prev) => (prev - 1 + caseStudies.length) % caseStudies.length);
  };

  return (
    <div className="services-page w-full overflow-x-hidden">

      {/* =========================================================================
          1. HERO SECTION (Dark Modern Aesthetic matching design)
          ========================================================================= */}
      <section className="relative bg-[#070b14] text-white pt-24 pb-20 lg:pt-32 lg:pb-28 overflow-hidden border-b border-slate-800/80">
        
        {/* Ambient Glow */}
        <div className="services-glow-sphere -top-20 -left-20 opacity-60"></div>
        <div className="services-glow-sphere top-1/2 right-0 opacity-40"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
            
            {/* Left Hero Content */}
            <div className="lg:col-span-6 space-y-6" data-aos="fade-right">
              
              {/* Eyebrow */}
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-extrabold text-[#f06600]">
                <span className="w-2 h-2 rounded-full bg-[#f06600] animate-ping inline-block"></span>
                <span>{servicesCMS.heroEyebrow || "OUR SERVICES"}</span>
              </div>

              {/* Main Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.15] text-white">
                {servicesCMS.heroTitleLine1 || "Complete Digital"} <br />
                {servicesCMS.heroTitleLine2 || "Solutions Under"} <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-orange-500 to-[#f06600]">
                  {servicesCMS.heroHighlight || "One Roof"}
                </span>
              </h1>

              {/* Subtitle */}
              <p className="text-slate-300 text-base sm:text-lg max-w-xl font-normal leading-relaxed">
                {servicesCMS.heroSubtitle || "From websites to AI solutions, digital marketing to custom applications — we provide end-to-end services to help your business grow, scale and stay ahead."}
              </p>

              {/* Buttons */}
              <div className="flex flex-wrap items-center gap-4 pt-2">
                <button
                  onClick={() => setQuoteModalOpen(true)}
                  className="bg-[#f06600] hover:bg-orange-600 text-white font-bold px-7 py-3.5 rounded-full shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all duration-300 flex items-center gap-2.5 text-sm sm:text-base cursor-pointer"
                >
                  <span>Discuss Your Project</span>
                  <BsArrowRight className="text-base" />
                </button>

                <button
                  onClick={() => setVideoModalOpen(true)}
                  className="glass-badge-dark hover:bg-slate-800/80 text-white font-semibold px-6 py-3.5 rounded-full border border-slate-700/80 transition-all duration-300 flex items-center gap-3 text-sm sm:text-base cursor-pointer group"
                >
                  <span className="w-8 h-8 rounded-full bg-white/10 group-hover:bg-[#f06600] flex items-center justify-center text-white transition-colors duration-300">
                    <BsPlayFill className="text-xl ml-0.5" />
                  </span>
                  <span>Watch Our Services</span>
                </button>
              </div>

              {/* 3 Core Highlights at Bottom */}
              <div className="pt-8 border-t border-slate-800/80 grid grid-cols-3 gap-4 text-xs sm:text-sm text-slate-300">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-orange-500/10 text-[#f06600] flex items-center justify-center shrink-0">
                    <BsBullseye className="text-base" />
                  </div>
                  <div>
                    <div className="font-bold text-white">Strategy</div>
                    <div className="text-slate-400 text-xs">First</div>
                  </div>
                </div>

                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-orange-500/10 text-[#f06600] flex items-center justify-center shrink-0">
                    <BsCpu className="text-base" />
                  </div>
                  <div>
                    <div className="font-bold text-white">Technology</div>
                    <div className="text-slate-400 text-xs">Driven</div>
                  </div>
                </div>

                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-orange-500/10 text-[#f06600] flex items-center justify-center shrink-0">
                    <BsGraphUpArrow className="text-base" />
                  </div>
                  <div>
                    <div className="font-bold text-white">Results</div>
                    <div className="text-slate-400 text-xs">Focused</div>
                  </div>
                </div>
              </div>

            </div>

            {/* Right Hero Visual Banner (Workstation with 3 monitors & Glowing BC logo) */}
            <div className="lg:col-span-6 relative" data-aos="fade-left">
              <div className="relative rounded-3xl overflow-hidden border border-slate-800 shadow-2xl bg-slate-900 group">
                <img 
                  src="/assets/services/services_hero_workstation.jpg" 
                  alt="Beta Communes Engineering Workstation"
                  className="w-full h-[400px] sm:h-[480px] object-cover object-center transition-transform duration-700 group-hover:scale-105"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/assets/hero_dev_workspace.jpg";
                  }}
                />

                {/* Subtle vignette gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#070b14]/90 via-transparent to-[#070b14]/30 pointer-events-none"></div>

                {/* Top-Right Callout */}
                <div className="absolute top-5 right-5 glass-badge-dark px-4 py-2 rounded-2xl flex items-center gap-2.5 text-xs text-white border border-white/10 shadow-xl">
                  <div className="w-6 h-6 rounded-lg bg-[#f06600] text-white flex items-center justify-center font-black text-xs shadow-md">
                    bc
                  </div>
                  <div className="leading-tight text-right">
                    <span className="font-extrabold text-white block text-[11px]">Your Growth</span>
                    <span className="text-orange-400 text-[10px] font-semibold">Our Services</span>
                  </div>
                </div>

                {/* Floating Vertical Right Card (Services List) */}
                <div className="absolute bottom-5 right-5 max-w-[220px] glass-badge-dark p-3.5 rounded-2xl border border-white/15 shadow-2xl backdrop-blur-md hidden sm:block">
                  <div className="text-[10px] uppercase tracking-wider text-[#f06600] font-extrabold mb-2">
                    Capabilities
                  </div>
                  <div className="space-y-1.5 text-xs font-semibold text-slate-200">
                    <div className="flex items-center gap-2 hover:text-[#f06600] transition-colors">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#f06600]"></span>
                      <span>Web Development</span>
                    </div>
                    <div className="flex items-center gap-2 hover:text-[#f06600] transition-colors">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#f06600]"></span>
                      <span>AI Solutions</span>
                    </div>
                    <div className="flex items-center gap-2 hover:text-[#f06600] transition-colors">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#f06600]"></span>
                      <span>Digital Marketing</span>
                    </div>
                    <div className="flex items-center gap-2 hover:text-[#f06600] transition-colors">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#f06600]"></span>
                      <span>Web Applications</span>
                    </div>
                    <div className="flex items-center gap-2 hover:text-[#f06600] transition-colors">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#f06600]"></span>
                      <span>UI/UX Design</span>
                    </div>
                    <div className="flex items-center gap-2 hover:text-[#f06600] transition-colors">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#f06600]"></span>
                      <span>Maintenance &amp; Support</span>
                    </div>
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>


      {/* =========================================================================
          2. TRUSTED BY AMBITIOUS BUSINESSES (Partner Bar)
          ========================================================================= */}
      <section className="bg-[#f8f9fc] border-b border-slate-200/80 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[11px] sm:text-xs font-bold uppercase tracking-widest text-slate-500 mb-6">
            TRUSTED BY AMBITIOUS BUSINESSES
          </p>

          <div className="flex flex-wrap items-center justify-center gap-8 sm:gap-12 lg:gap-16 opacity-70 grayscale hover:grayscale-0 transition-all duration-300">
            {/* Shopify */}
            <div className="flex items-center gap-2 font-bold text-slate-700 text-lg hover:text-emerald-600 transition-colors">
              <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
                <path d="M19.78 6.55c-.06-.39-.4-.68-.8-.68l-3.35-.12-1.78-3.52c-.17-.34-.52-.55-.9-.55s-.73.21-.9.55l-1.8 3.52-3.33.12c-.4 0-.74.29-.8.68L5.3 18.28c-.04.28.06.56.26.75.2.19.48.29.76.26l12.44-1.24c.28-.03.52-.19.66-.43.14-.24.15-.54.04-.8L19.78 6.55zM12 4.25l1.37 2.71c.09.18.27.3.47.31l2.58.09-1.87 1.83c-.15.14-.21.35-.17.55l.44 2.57-2.31-1.21c-.18-.09-.39-.09-.57 0l-2.31 1.21.44-2.57c.04-.2-.02-.41-.17-.55L7.58 7.36l2.58-.09c.2-.01.38-.13.47-.31L12 4.25z"/>
              </svg>
              <span>shopify</span>
            </div>

            {/* Google */}
            <div className="flex items-center gap-1.5 font-bold text-slate-700 text-lg hover:text-blue-600 transition-colors">
              <span className="text-blue-500 font-black">G</span>
              <span className="text-red-500 font-black">o</span>
              <span className="text-yellow-500 font-black">o</span>
              <span className="text-blue-500 font-black">g</span>
              <span className="text-green-500 font-black">l</span>
              <span className="text-red-500 font-black">e</span>
            </div>

            {/* Meta */}
            <div className="flex items-center gap-2 font-bold text-slate-700 text-lg hover:text-blue-500 transition-colors">
              <svg className="w-6 h-6 fill-current text-blue-600" viewBox="0 0 24 24">
                <path d="M12 8.5C9.7 4.7 6.6 3 4.2 3 1.9 3 0 5 0 8.5c0 4.6 3.4 8.7 6.5 8.7 2.4 0 4.2-1.9 5.5-4.4 1.3 2.5 3.1 4.4 5.5 4.4 3.1 0 6.5-4.1 6.5-8.7C24 5 22.1 3 19.8 3c-2.4 0-5.5 1.7-7.8 5.5zm-5.5 6.7c-2.1 0-4.5-3.1-4.5-6.7 0-2.4 1.2-3.5 2.2-3.5 1.7 0 4 1.8 5.6 5.5-1 2.9-2.2 4.7-3.3 4.7zm11 0c-1.1 0-2.3-1.8-3.3-4.7 1.6-3.7 3.9-5.5 5.6-5.5 1 0 2.2 1.1 2.2 3.5 0 3.6-2.4 6.7-4.5 6.7z"/>
              </svg>
              <span>Meta</span>
            </div>

            {/* Figma */}
            <div className="flex items-center gap-2 font-bold text-slate-700 text-lg hover:text-purple-600 transition-colors">
              <span className="font-extrabold tracking-tight">Figma</span>
            </div>

            {/* WordPress */}
            <div className="flex items-center gap-2 font-bold text-slate-700 text-lg hover:text-sky-700 transition-colors">
              <span className="w-5 h-5 rounded-full border-2 border-current flex items-center justify-center text-xs font-black">W</span>
              <span>WORDPRESS</span>
            </div>

            {/* Laravel */}
            <div className="flex items-center gap-2 font-bold text-slate-700 text-lg hover:text-red-600 transition-colors">
              <span className="font-extrabold tracking-tight">Laravel</span>
            </div>
          </div>
        </div>
      </section>


      {/* =========================================================================
          3. CORE SERVICES GRID (Light Theme)
          ========================================================================= */}
      <section className="py-20 lg:py-28 bg-white border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-16 gap-6" data-aos="fade-up">
            <div>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-extrabold text-[#f06600] mb-2">
                <span className="w-2 h-2 rounded-full bg-[#f06600] inline-block"></span>
                <span>OUR SERVICES</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-slate-900 leading-[1.2]">
                Services Designed <br className="hidden sm:block" />
                for Real <span className="text-[#f06600]">Business Growth</span>
              </h2>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-5">
              <p className="text-slate-600 text-sm sm:text-base max-w-md font-normal leading-relaxed">
                We combine creativity, technology and data to deliver digital solutions that create measurable impact. Explore our core services below.
              </p>
              <button
                onClick={() => setQuoteModalOpen(true)}
                className="bg-[#f06600] hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-full shadow-lg shadow-orange-500/25 transition-all flex items-center gap-2 text-xs sm:text-sm self-start sm:self-auto shrink-0 cursor-pointer"
              >
                <span>Get a Custom Quote</span>
                <BsArrowRight />
              </button>
            </div>
          </div>

          {/* 6 Cards Grid (3 Columns) */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {coreServices.map((svc, index) => (
              <div
                key={svc.id}
                className="service-feature-card bg-white border border-slate-200/90 rounded-3xl overflow-hidden flex flex-col justify-between shadow-sm"
                data-aos="fade-up"
                data-aos-delay={index * 80}
              >
                <div>
                  {/* Media Header Preview */}
                  <div className="h-52 w-full overflow-hidden bg-slate-950 relative">
                    <img 
                      src={svc.image} 
                      alt={svc.title}
                      className="card-media-img w-full h-full object-cover object-center"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/assets/hero_dev_workspace.jpg";
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent"></div>
                    
                    {/* Icon Badge */}
                    <div className="absolute bottom-4 left-4 w-12 h-12 rounded-2xl bg-[#f06600] text-white flex items-center justify-center text-2xl shadow-xl">
                      {svc.icon}
                    </div>
                  </div>

                  {/* Body Content */}
                  <div className="p-6 sm:p-7">
                    <h3 className="text-xl font-black text-slate-900 mb-2">
                      {svc.title}
                    </h3>
                    <p className="text-slate-600 text-xs sm:text-sm leading-relaxed mb-6 font-normal">
                      {svc.desc}
                    </p>

                    {/* Features List */}
                    <div className="space-y-2.5 pt-4 border-t border-slate-100">
                      {svc.features.map((feat, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                          <BsCheckCircleFill className="text-[#f06600] text-xs shrink-0" />
                          <span>{feat}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Footer Link */}
                <div className="p-6 pt-0">
                  <Link
                    to={svc.link}
                    className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold text-[#f06600] hover:text-orange-700 transition-colors group"
                  >
                    <span>Learn More</span>
                    <BsArrowRight className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>

              </div>
            ))}
          </div>

        </div>
      </section>


      {/* =========================================================================
          4. OUR PROCESS (Light Theme)
          ========================================================================= */}
      <section className="py-24 bg-[#fbfcfe] border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-16 gap-6" data-aos="fade-up">
            <div>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-extrabold text-[#f06600] mb-2">
                <span className="w-2 h-2 rounded-full bg-[#f06600] inline-block"></span>
                <span>OUR PROCESS</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-slate-900 leading-[1.2]">
                {servicesCMS.processHeading || "A Simple Process for Amazing Results"}
              </h2>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-5">
              <p className="text-slate-600 text-sm sm:text-base max-w-md font-normal leading-relaxed">
                {servicesCMS.processSubtitle || "We follow a clear and collaborative process to turn your ideas into successful digital products."}
              </p>
              <button
                onClick={() => setQuoteModalOpen(true)}
                className="bg-slate-900 hover:bg-slate-800 text-white font-bold px-6 py-3 rounded-full shadow-md transition-all flex items-center gap-2 text-xs sm:text-sm self-start sm:self-auto shrink-0 cursor-pointer"
              >
                <span>Start Your Project</span>
                <BsArrowRight className="text-[#f06600]" />
              </button>
            </div>
          </div>

          {/* Desktop 5-Step Process Roadmap (lg and above) */}
          <div className="hidden lg:block relative pt-6 pb-6">
            {/* Connecting Track Line */}
            <div className="process-track-line"></div>

            <div className="grid grid-cols-5 gap-8 relative z-10">
              {activeProcessSteps.map((step, idx) => {
                const stepIcons = [
                  <BsSearch className="text-xl" />,
                  <BsClipboardCheck className="text-xl" />,
                  <BsPalette className="text-xl" />,
                  <BsCodeSlash className="text-xl" />,
                  <BsRocketTakeoff className="text-xl" />
                ];
                return (
                  <div key={step.num || idx} className="process-step-node text-center" data-aos="fade-up" data-aos-delay={idx * 80}>
                    <div className="process-step-badge">
                      {stepIcons[idx % stepIcons.length]}
                    </div>
                    <div className="text-base font-black text-slate-900 tracking-tight">
                      {step.num}. {step.title}
                    </div>
                    <p className="text-slate-500 text-xs mt-1.5 leading-relaxed">
                      {step.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mobile & Tablet Milestone Vertical Timeline (< lg: single card in one row with side line) */}
          <div className="block lg:hidden relative pt-2 pb-2">
            {/* Continuous Vertical Connecting Line */}
            <div className="absolute left-[17px] sm:left-[21px] top-4 bottom-4 w-0.5 bg-gradient-to-b from-[#f06600] via-orange-400 to-orange-200"></div>

            <div className="space-y-4 sm:space-y-6 relative z-10">
              {activeProcessSteps.map((step, sIdx) => {
                const stepIcons = [
                  <BsSearch className="text-base" />,
                  <BsClipboardCheck className="text-base" />,
                  <BsPalette className="text-base" />,
                  <BsCodeSlash className="text-base" />,
                  <BsRocketTakeoff className="text-base" />
                ];
                return (
                  <div key={step.num || sIdx} className="relative flex items-start pl-11 sm:pl-14 group" data-aos="fade-up" data-aos-delay={sIdx * 60}>
                    {/* Milestone dot on the vertical side line */}
                    <div className="absolute left-[8px] sm:left-[12px] top-3.5 w-5 h-5 rounded-full bg-white border-2 border-[#f06600] shadow-[0_0_0_2px_#fed7aa] z-10 flex items-center justify-center transition-transform duration-300 group-hover:scale-125">
                      <div className="w-2 h-2 rounded-full bg-[#f06600]"></div>
                    </div>

                    {/* Single Card per Row */}
                    <div className="flex-1 bg-white border border-slate-200/90 rounded-2xl p-4 sm:p-5 shadow-sm hover:shadow-md hover:border-orange-300 transition-all duration-300">
                      <div className="flex items-center justify-between gap-3 mb-1.5 flex-wrap">
                        <div className="flex items-center gap-2.5">
                          <span className="text-xs font-black uppercase tracking-wider text-[#f06600] bg-orange-50 px-2.5 py-1 rounded-lg border border-orange-200/70">
                            Step {step.num}
                          </span>
                          <h3 className="font-extrabold text-slate-900 text-sm sm:text-base group-hover:text-[#f06600] transition-colors">
                            {step.title}
                          </h3>
                        </div>
                        <div className="w-8 h-8 rounded-xl bg-orange-500/10 text-[#f06600] flex items-center justify-center text-sm">
                          {stepIcons[sIdx % stepIcons.length]}
                        </div>
                      </div>
                      <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                        {step.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      </section>


      {/* =========================================================================
          5. WHY CHOOSE US (Dark Theme)
          ========================================================================= */}
      <section className="py-24 bg-[#050811] text-white relative border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-16 gap-8" data-aos="fade-up">
            <div>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-extrabold text-[#f06600] mb-2">
                <span className="w-2 h-2 rounded-full bg-[#f06600] inline-block"></span>
                <span>WHY CHOOSE US</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white leading-[1.2]">
                More Than <br className="hidden sm:block" />
                Just a <span className="text-[#f06600]">Service Provider</span>
              </h2>
              <p className="text-slate-400 text-sm sm:text-base mt-3 max-w-md">
                We're a long-term partner committed to your success.
              </p>
            </div>

            {/* Neon BC Board Tag */}
            <div className="glass-badge-dark p-4 px-6 rounded-2xl border border-white/10 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#f06600] text-white flex items-center justify-center font-black text-sm shadow-lg">
                bc
              </div>
              <div className="text-xs">
                <div className="text-white font-extrabold">Ideas · Strategy</div>
                <div className="text-orange-400 font-semibold">Execution · Growth</div>
              </div>
            </div>
          </div>

          {/* 5 Value Badges */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
            
            <div className="bg-[#0b101c] p-6 rounded-2xl border border-slate-800 text-center hover:border-[#f06600]/50 transition-all" data-aos="fade-up" data-aos-delay="0">
              <div className="w-12 h-12 rounded-xl bg-orange-500/10 text-[#f06600] mx-auto flex items-center justify-center text-xl mb-3">
                <BsBriefcase />
              </div>
              <div className="text-sm font-bold text-white leading-tight">Business-Focused</div>
              <div className="text-xs text-slate-400 mt-1">Solutions</div>
            </div>

            <div className="bg-[#0b101c] p-6 rounded-2xl border border-slate-800 text-center hover:border-[#f06600]/50 transition-all" data-aos="fade-up" data-aos-delay="80">
              <div className="w-12 h-12 rounded-xl bg-orange-500/10 text-[#f06600] mx-auto flex items-center justify-center text-xl mb-3">
                <BsPeopleFill />
              </div>
              <div className="text-sm font-bold text-white leading-tight">Experienced</div>
              <div className="text-xs text-slate-400 mt-1">Team</div>
            </div>

            <div className="bg-[#0b101c] p-6 rounded-2xl border border-slate-800 text-center hover:border-[#f06600]/50 transition-all" data-aos="fade-up" data-aos-delay="160">
              <div className="w-12 h-12 rounded-xl bg-orange-500/10 text-[#f06600] mx-auto flex items-center justify-center text-xl mb-3">
                <BsClipboardCheck />
              </div>
              <div className="text-sm font-bold text-white leading-tight">Transparent</div>
              <div className="text-xs text-slate-400 mt-1">Process</div>
            </div>

            <div className="bg-[#0b101c] p-6 rounded-2xl border border-slate-800 text-center hover:border-[#f06600]/50 transition-all" data-aos="fade-up" data-aos-delay="240">
              <div className="w-12 h-12 rounded-xl bg-orange-500/10 text-[#f06600] mx-auto flex items-center justify-center text-xl mb-3">
                <BsClock />
              </div>
              <div className="text-sm font-bold text-white leading-tight">On-Time</div>
              <div className="text-xs text-slate-400 mt-1">Delivery</div>
            </div>

            <div className="bg-[#0b101c] p-6 rounded-2xl border border-slate-800 text-center hover:border-[#f06600]/50 transition-all col-span-2 sm:col-span-1" data-aos="fade-up" data-aos-delay="320">
              <div className="w-12 h-12 rounded-xl bg-orange-500/10 text-[#f06600] mx-auto flex items-center justify-center text-xl mb-3">
                <BsShieldFillCheck />
              </div>
              <div className="text-sm font-bold text-white leading-tight">Long-Term</div>
              <div className="text-xs text-slate-400 mt-1">Support</div>
            </div>

          </div>

        </div>
      </section>


      {/* =========================================================================
          6. FEATURED CASE STUDIES (Light Theme)
          ========================================================================= */}
      <section className="py-24 bg-white border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-16 gap-6" data-aos="fade-up">
            <div>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-extrabold text-[#f06600] mb-2">
                <span className="w-2 h-2 rounded-full bg-[#f06600] inline-block"></span>
                <span>FEATURED CASE STUDIES</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-slate-900 leading-[1.2]">
                Real Businesses. <br className="hidden sm:block" />
                <span className="text-[#f06600]">Real Results.</span>
              </h2>
            </div>

            <div className="flex items-center gap-4">
              <p className="text-slate-600 text-sm sm:text-base max-w-sm font-normal leading-relaxed hidden sm:block">
                See how our services have helped businesses across different industries grow and succeed.
              </p>
              
              <div className="flex items-center gap-2">
                <button 
                  onClick={handlePrevCase}
                  className="w-10 h-10 rounded-full border border-slate-200 text-slate-700 hover:text-[#f06600] hover:border-[#f06600] flex items-center justify-center transition-colors cursor-pointer"
                >
                  <BsChevronLeft />
                </button>
                <button 
                  onClick={handleNextCase}
                  className="w-10 h-10 rounded-full border border-slate-200 text-slate-700 hover:text-[#f06600] hover:border-[#f06600] flex items-center justify-center transition-colors cursor-pointer"
                >
                  <BsChevronRight />
                </button>
                <Link
                  to="/portfolio"
                  className="ml-2 inline-flex items-center gap-2 text-xs font-bold text-[#f06600] hover:text-orange-700"
                >
                  <span>View All Projects</span>
                  <BsArrowRight />
                </Link>
              </div>
            </div>
          </div>

          {/* 4 Case Studies Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {caseStudies.map((cs, idx) => (
              <Link
                key={cs.id}
                to={cs.link}
                className="case-study-card bg-white rounded-2xl overflow-hidden border border-slate-200/90 shadow-sm flex flex-col group cursor-pointer"
                data-aos="fade-up"
                data-aos-delay={idx * 80}
              >
                <div className="h-52 w-full overflow-hidden bg-slate-950 relative">
                  <img 
                    src={cs.image} 
                    alt={cs.title}
                    className="case-img w-full h-full object-cover object-center"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/assets/hero_dev_workspace.jpg";
                    }}
                  />
                  <div className="absolute inset-0 bg-slate-950/20 group-hover:bg-transparent transition-colors"></div>
                </div>

                <div className="p-5 flex flex-col justify-between flex-grow">
                  <div>
                    <h3 className="text-base font-extrabold text-slate-900 group-hover:text-[#f06600] transition-colors leading-snug">
                      {cs.title}
                    </h3>
                    <p className="text-xs font-semibold text-slate-500 mt-1">
                      {cs.category}
                    </p>
                  </div>

                  <div className="pt-3 mt-3 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-[#f06600]">
                    <span>Case Details</span>
                    <BsArrowRight className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

        </div>
      </section>


      {/* =========================================================================
          7. CALL TO ACTION (Vibrant Orange Gradient Banner)
          ========================================================================= */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            className="rounded-3xl p-8 sm:p-12 bg-gradient-to-r from-[#f06600] via-[#f77f00] to-[#ff9900] text-white shadow-2xl shadow-orange-500/25 flex flex-col lg:flex-row items-center justify-between gap-8"
            data-aos="zoom-in"
          >
            <div className="space-y-3 text-center lg:text-left">
              <div className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-orange-100 bg-black/15 px-3 py-1 rounded-full">
                <span>➔</span>
                <span>LET'S WORK TOGETHER</span>
              </div>
              <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white">
                Ready to Bring Your Ideas to Life?
              </h2>
              <p className="text-orange-100 text-sm sm:text-base font-medium">
                Let's create powerful digital solutions for your business.
              </p>
            </div>

            <Link
              to="/contactus"
              className="bg-white hover:bg-slate-50 text-slate-900 font-extrabold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2.5 text-sm sm:text-base cursor-pointer shrink-0"
            >
              <span>Get Started Today</span>
              <BsArrowRight className="text-base text-[#f06600]" />
            </Link>
          </div>
        </div>
      </section>


      {/* =========================================================================
          8. INTERACTIVE CONSULTATION / QUOTE MODAL
          ========================================================================= */}
      {quoteModalOpen && (
        <div 
          className="fixed inset-0 bg-slate-950/80 backdrop-blur-md z-50 flex items-center justify-center p-4"
          onClick={() => setQuoteModalOpen(false)}
        >
          <div 
            className="bg-white rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl relative border border-slate-100"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={() => setQuoteModalOpen(false)}
              className="absolute top-5 right-5 text-slate-400 hover:text-slate-800 text-2xl"
            >
              <BsX />
            </button>

            <div className="text-xs uppercase tracking-widest font-extrabold text-[#f06600] mb-1">
              FAST ESTIMATE
            </div>
            <h3 className="text-2xl font-black text-slate-900 mb-2">
              Discuss Your Project
            </h3>
            <p className="text-slate-500 text-xs sm:text-sm mb-6 leading-relaxed">
              Tell us about your technical requirements and our engineering team will get back to you within 24 hours.
            </p>

            <form 
              onSubmit={(e) => {
                e.preventDefault();
                alert("Thank you! We have received your inquiry and will contact you promptly.");
                setQuoteModalOpen(false);
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Your Name</label>
                <input 
                  type="text" 
                  required 
                  placeholder="e.g. John Doe"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#f06600]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Work Email</label>
                <input 
                  type="email" 
                  required 
                  placeholder="name@company.com"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#f06600]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Service Needed</label>
                <select className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#f06600]">
                  <option value="web">Web Development</option>
                  <option value="ai">AI Solutions &amp; Automation</option>
                  <option value="marketing">Digital Marketing</option>
                  <option value="webapp">Custom Web Applications</option>
                  <option value="uiux">UI/UX Design</option>
                  <option value="support">Maintenance &amp; Support</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Project Details</label>
                <textarea 
                  rows={3} 
                  required
                  placeholder="Briefly describe your project or objectives..."
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#f06600]"
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full bg-[#f06600] hover:bg-orange-600 text-white font-bold py-3 rounded-xl transition-all shadow-md mt-2"
              >
                Send Request ➔
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {videoModalOpen && (
        <div 
          className="fixed inset-0 bg-slate-950/85 backdrop-blur-md z-50 flex items-center justify-center p-4"
          onClick={() => setVideoModalOpen(false)}
        >
          <div 
            className="relative w-full max-w-4xl bg-slate-900 rounded-3xl overflow-hidden border border-slate-700 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-950">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-[#f06600]"></span>
                <span className="text-xs font-bold uppercase tracking-wider text-white">
                  Beta Communes · Our Services Showcase
                </span>
              </div>
              <button 
                onClick={() => setVideoModalOpen(false)}
                className="text-slate-400 hover:text-white text-2xl cursor-pointer"
              >
                <BsX />
              </button>
            </div>
            <div className="relative aspect-video bg-black flex items-center justify-center">
              <iframe
                className="w-full h-full"
                src="https://www.youtube-nocookie.com/embed/dQw4w9WgXcQ?autoplay=1"
                title="Services Showcase"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Services;